import {
  AssessmentFieldType,
  AssessmentOptionDisplayMode,
  AssessmentOptionSetType,
  DynamicAssessmentConstants,
  type AssessmentTemplateFieldDetail,
  type AssessmentTemplateOptionDetail,
  type AssessmentTemplateOptionSetDetail,
  type AssessmentTemplateSectionDetail,
  type AssessmentTemplateVersionDetail,
  type AssessmentTemplateVersionUsage,
  type SaveAssessmentTemplateVersionRequest,
} from '../types/dynamicAssessment';
import { DEFAULT_ASSESSMENT_FORM_UI_CONFIG } from './assessmentFormConfig';

export interface TemplateEditorState {
  versionId?: number;
  templateId: number;
  templateName: string;
  versionNumber: number;
  status: string;
  changeNotes?: string;
  optionSets: AssessmentTemplateOptionSetDetail[];
  sections: AssessmentTemplateSectionDetail[];
}

export function createEditorStateFromVersion(
  version: AssessmentTemplateVersionDetail
): TemplateEditorState {
  return {
    versionId: version.id,
    templateId: version.templateId,
    templateName: version.templateName,
    versionNumber: version.versionNumber,
    status: version.status,
    changeNotes: version.changeNotes,
    optionSets: JSON.parse(JSON.stringify(version.optionSets ?? [])),
    sections: JSON.parse(JSON.stringify(version.sections ?? [])),
  };
}

export function isFieldInUse(
  fieldCode: string,
  usage?: AssessmentTemplateVersionUsage
): boolean {
  return (
    usage?.usedFields?.some(
      (field) => field.fieldCode.toLowerCase() === fieldCode.toLowerCase()
    ) ?? false
  );
}

export function isOptionInUse(
  optionValue: number,
  usage?: AssessmentTemplateVersionUsage
): boolean {
  return usage?.usedOptions?.some((option) => option.optionValue === optionValue) ?? false;
}

export function getOptionUsageCount(
  optionValue: number,
  usage?: AssessmentTemplateVersionUsage
): number {
  return (
    usage?.usedOptions?.find((option) => option.optionValue === optionValue)?.responseCount ?? 0
  );
}

export function reorderOptions(
  optionSetIndex: number,
  optionIndex: number,
  direction: 'up' | 'down',
  editor: TemplateEditorState
): TemplateEditorState {
  const next = cloneEditor(editor);
  const options = [...(next.optionSets[optionSetIndex]?.options ?? [])];
  const targetIndex = direction === 'up' ? optionIndex - 1 : optionIndex + 1;

  if (targetIndex < 0 || targetIndex >= options.length) {
    return editor;
  }

  [options[optionIndex], options[targetIndex]] = [options[targetIndex], options[optionIndex]];
  options.forEach((option, index) => {
    option.displayOrder = index + 1;
  });

  next.optionSets[optionSetIndex].options = options;
  return next;
}

export function softDeleteOption(
  optionSetIndex: number,
  optionIndex: number,
  editor: TemplateEditorState,
  usage?: AssessmentTemplateVersionUsage
): { editor: TemplateEditorState; error?: string } {
  const option = editor.optionSets[optionSetIndex]?.options?.[optionIndex];
  if (!option) {
    return { editor };
  }

  if (isOptionInUse(option.optionValue, usage)) {
    return {
      editor,
      error: `Cannot remove "${option.label}" because it is used in ${getOptionUsageCount(option.optionValue, usage)} assessment response(s). Deactivate it instead.`,
    };
  }

  const next = cloneEditor(editor);
  const activeCount = next.optionSets[optionSetIndex].options.filter((o) => o.isActive !== false).length;
  if (activeCount <= 1 && option.isActive !== false) {
    return { editor, error: 'At least one active maturity option is required.' };
  }

  next.optionSets[optionSetIndex].options = next.optionSets[optionSetIndex].options.filter(
    (_, index) => index !== optionIndex
  );
  next.optionSets[optionSetIndex].options.forEach((item, index) => {
    item.displayOrder = index + 1;
  });

  return { editor: next };
}

export function deactivateOption(
  optionSetIndex: number,
  optionIndex: number,
  editor: TemplateEditorState
): { editor: TemplateEditorState; error?: string } {
  const next = cloneEditor(editor);
  const options = next.optionSets[optionSetIndex]?.options ?? [];
  const option = options[optionIndex];
  if (!option) {
    return { editor };
  }

  const activeCount = options.filter((o) => o.isActive !== false).length;
  if (activeCount <= 1 && option.isActive !== false) {
    return { editor, error: 'At least one active maturity option is required.' };
  }

  option.isActive = false;
  return { editor: next };
}

export function addMaturityOption(
  optionSetIndex: number,
  editor: TemplateEditorState
): TemplateEditorState {
  const next = cloneEditor(editor);
  const options = next.optionSets[optionSetIndex]?.options ?? [];
  const nextValue = options.reduce((max, option) => Math.max(max, option.optionValue), 0) + 1;

  options.push({
    optionValue: nextValue,
    label: `Level ${nextValue}`,
    definition: '',
    displayOrder: options.length + 1,
    isDefault: false,
    isActive: true,
  });

  next.optionSets[optionSetIndex].options = options;
  return next;
}

export function softDeleteField(
  sectionIndex: number,
  fieldIndex: number,
  editor: TemplateEditorState,
  usage?: AssessmentTemplateVersionUsage
): { editor: TemplateEditorState; error?: string } {
  const field = editor.sections[sectionIndex]?.fields?.[fieldIndex];
  if (!field) {
    return { editor };
  }

  if (isFieldInUse(field.fieldCode, usage)) {
    return {
      editor,
      error: `Cannot remove field "${field.fieldCode}" because it is used in assessments. Deactivate it instead.`,
    };
  }

  const next = cloneEditor(editor);
  next.sections[sectionIndex].fields = next.sections[sectionIndex].fields.filter(
    (_, index) => index !== fieldIndex
  );
  return { editor: next };
}

export function deactivateField(
  sectionIndex: number,
  fieldIndex: number,
  editor: TemplateEditorState
): TemplateEditorState {
  const next = cloneEditor(editor);
  const field = next.sections[sectionIndex]?.fields?.[fieldIndex];
  if (field) {
    field.isActive = false;
  }
  return next;
}

export function validateTemplateEditor(editor: TemplateEditorState): string | null {
  if (!editor.sections.length) {
    return 'At least one section is required.';
  }

  const fieldCodes = new Set<string>();
  for (const section of editor.sections) {
    if (!section.title?.trim()) {
      return 'Each section must have a title.';
    }

    for (const field of section.fields) {
      if (!field.fieldCode?.trim()) {
        return 'Each question must have a field code.';
      }

      const normalized = field.fieldCode.trim().toLowerCase();
      if (fieldCodes.has(normalized)) {
        return `Duplicate field code "${field.fieldCode}".`;
      }
      fieldCodes.add(normalized);

      if (field.isActive !== false && !field.label?.trim()) {
        return `Active question "${field.fieldCode}" must have a label.`;
      }
    }
  }

  for (const optionSet of editor.optionSets) {
    const activeOptions = optionSet.options.filter((option) => option.isActive !== false);
    if (!activeOptions.length) {
      return 'At least one active maturity option is required.';
    }

    const values = new Set<number>();
    for (const option of optionSet.options) {
      if (option.isActive !== false && !option.label?.trim()) {
        return 'Active maturity options must have a label.';
      }

      if (!values.has(option.optionValue)) {
        values.add(option.optionValue);
      } else {
        return `Duplicate maturity option value ${option.optionValue}.`;
      }

      if (option.isActive !== false && !option.definition?.trim()) {
        return `Active maturity option "${option.label}" should include a definition.`;
      }
    }
  }

  return null;
}

export function buildSaveTemplateVersionRequest(
  editor: TemplateEditorState
): SaveAssessmentTemplateVersionRequest {
  return {
    versionId: editor.versionId,
    templateId: editor.templateId,
    changeNotes: editor.changeNotes,
    sections: editor.sections.map((section, sectionIndex) => ({
      ...section,
      displayOrder: sectionIndex + 1,
      fields: section.fields.map((field, fieldIndex) => ({
        ...field,
        displayOrder: fieldIndex + 1,
      })),
    })),
    optionSets: editor.optionSets.map((optionSet) => ({
      ...optionSet,
      options: optionSet.options.map((option, optionIndex) => ({
        ...option,
        displayOrder: optionIndex + 1,
      })),
    })),
  };
}

export function buildDraftFromPublishedVersion(
  version: AssessmentTemplateVersionDetail,
  changeNotes?: string
): SaveAssessmentTemplateVersionRequest {
  const editor = createEditorStateFromVersion(version);
  editor.versionId = undefined;
  editor.changeNotes = changeNotes ?? `Draft created from published v${version.versionNumber}`;
  return buildSaveTemplateVersionRequest(editor);
}

function cloneEditor(editor: TemplateEditorState): TemplateEditorState {
  return JSON.parse(JSON.stringify(editor));
}

export function getMaturityOptionSetIndex(editor: TemplateEditorState): number {
  return editor.optionSets.findIndex((set) =>
    set.options?.some((option) =>
      DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions.some(
        (defaultOption) => defaultOption.optionValue === option.optionValue
      )
    )
  );
}

export function getRatingFieldIndices(editor: TemplateEditorState): number[] {
  const indices: number[] = [];
  editor.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.fieldType === AssessmentFieldType.Rating && field.optionSetIndex != null) {
        indices.push(field.optionSetIndex);
      }
    });
  });
  return [...new Set(indices)];
}

export const DEFAULT_OPTION_SET: AssessmentTemplateOptionSetDetail = {
  name: '5-Point Maturity Scale',
  optionSetType: AssessmentOptionSetType.Ordinal,
  minValue: 1,
  maxValue: 5,
  allowHalfSteps: false,
  displayMode: AssessmentOptionDisplayMode.Slider,
  options: DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions.map((option, index) => ({
    optionValue: option.optionValue,
    label: option.label,
    definition: option.definition,
    displayOrder: index + 1,
    isDefault: option.optionValue === 3,
    isActive: true,
  })),
};

export function createDefaultSections(): AssessmentTemplateSectionDetail[] {
  const config = DEFAULT_ASSESSMENT_FORM_UI_CONFIG;
  return [
    {
      code: DynamicAssessmentConstants.sectionCodes.currentState,
      title: config.currentSection.title,
      description: config.currentSection.description,
      displayOrder: 1,
      isCollapsible: true,
      isActive: true,
      fields: [
        {
          fieldCode: DynamicAssessmentConstants.fieldCodes.currentRating,
          fieldType: AssessmentFieldType.Rating,
          label: 'What is the current maturity rating?',
          isRequired: true,
          displayOrder: 1,
          optionSetIndex: 0,
          isActive: true,
          legacyMap: {
            legacyTable: DynamicAssessmentConstants.legacySubCapabilityDataTable,
            legacyColumn: 'CurrentStatusRating',
            transformRule: 0,
          },
        },
        {
          fieldCode: DynamicAssessmentConstants.fieldCodes.currentDescription,
          fieldType: AssessmentFieldType.TextArea,
          label: 'Describe the current state',
          placeholder: config.currentSection.commentPlaceholder,
          isRequired: true,
          displayOrder: 2,
          isActive: true,
          legacyMap: {
            legacyTable: DynamicAssessmentConstants.legacySubCapabilityDataTable,
            legacyColumn: 'CurrentStatusDescription',
            transformRule: 0,
          },
        },
      ],
    },
    {
      code: DynamicAssessmentConstants.sectionCodes.futureState,
      title: config.futureSection.title,
      description: config.futureSection.description,
      displayOrder: 2,
      isCollapsible: true,
      isActive: true,
      fields: [
        {
          fieldCode: DynamicAssessmentConstants.fieldCodes.futureRating,
          fieldType: AssessmentFieldType.Rating,
          label: 'What is the future maturity rating?',
          isRequired: true,
          displayOrder: 1,
          optionSetIndex: 0,
          isActive: true,
          legacyMap: {
            legacyTable: DynamicAssessmentConstants.legacySubCapabilityDataTable,
            legacyColumn: 'FutureStatusRating',
            transformRule: 0,
          },
        },
        {
          fieldCode: DynamicAssessmentConstants.fieldCodes.futureDescription,
          fieldType: AssessmentFieldType.TextArea,
          label: 'Describe the future state',
          placeholder: config.futureSection.commentPlaceholder,
          isRequired: true,
          displayOrder: 2,
          isActive: true,
          legacyMap: {
            legacyTable: DynamicAssessmentConstants.legacySubCapabilityDataTable,
            legacyColumn: 'FutureStatusDescription',
            transformRule: 0,
          },
        },
      ],
    },
    {
      code: DynamicAssessmentConstants.sectionCodes.businessValue,
      title: config.businessValueTitle,
      description: 'Capture business value considerations.',
      displayOrder: 3,
      isCollapsible: true,
      isActive: true,
      fields: [
        {
          fieldCode: DynamicAssessmentConstants.fieldCodes.businessValue,
          fieldType: AssessmentFieldType.TextArea,
          label: config.businessValueTitle,
          placeholder: config.businessValuePlaceholder,
          isRequired: false,
          displayOrder: 1,
          isActive: true,
          legacyMap: {
            legacyTable: DynamicAssessmentConstants.legacySubCapabilityDataTable,
            legacyColumn: 'BusinessValue',
            transformRule: 0,
          },
        },
        {
          fieldCode: DynamicAssessmentConstants.fieldCodes.businessValueImpact,
          fieldType: AssessmentFieldType.Number,
          label: config.businessImpactTitle,
          placeholder: config.businessImpactPlaceholder,
          isRequired: false,
          displayOrder: 2,
          isActive: true,
          legacyMap: {
            legacyTable: DynamicAssessmentConstants.legacySubCapabilityDataTable,
            legacyColumn: 'BusinessValueImpact',
            transformRule: 0,
          },
        },
      ],
    },
  ];
}

export function updateField(
  editor: TemplateEditorState,
  sectionIndex: number,
  fieldIndex: number,
  patch: Partial<AssessmentTemplateFieldDetail>
): TemplateEditorState {
  const next = cloneEditor(editor);
  next.sections[sectionIndex].fields[fieldIndex] = {
    ...next.sections[sectionIndex].fields[fieldIndex],
    ...patch,
  };
  return next;
}

export function updateOption(
  editor: TemplateEditorState,
  optionSetIndex: number,
  optionIndex: number,
  patch: Partial<AssessmentTemplateOptionDetail>
): TemplateEditorState {
  const next = cloneEditor(editor);
  next.optionSets[optionSetIndex].options[optionIndex] = {
    ...next.optionSets[optionSetIndex].options[optionIndex],
    ...patch,
  };
  return next;
}

export function updateSection(
  editor: TemplateEditorState,
  sectionIndex: number,
  patch: Partial<AssessmentTemplateSectionDetail>
): TemplateEditorState {
  const next = cloneEditor(editor);
  next.sections[sectionIndex] = {
    ...next.sections[sectionIndex],
    ...patch,
  };
  return next;
}
